// @flow

import R from 'ramda';
import React from 'react';

import {
  DRUID_TO_HUMAN_READABLE,
  ALL_EVENT_NAMES,
  COMMERCE_EVENT_NAMES
} from '../eo_mapping';
import { toPercentageStr, roundToFixedN } from '../../../main/support/utils';

import type { Columns, Row } from '../../../main/types/tableTypes';
import { getListOfDimensionsAndValues } from './druid/nativeQueries';
import { EO_CUSTOM_EVENT } from '../eo_mapping/datasources';

/**
 * Default metadata based on column type
 */
const _TYPE_METADATA: Object = {
  dimension: {
    formatter: R.identity
  },
  data_sources: {
    formatter: v => (v || 0).toLocaleString('en-US')
  },
  eo_custom_event: {
    formatter: v => (v || 0).toLocaleString('en-US')
  },
  eo_custom_event_blocked: {
    formatter: v => (v || 0).toLocaleString('en-US')
  },
  eo_commerce_event: {
    formatter: v => (v || 0).toLocaleString('en-US')
  },
  formula: {
    formatter: v => {
      const n = v > 100 ? 2 : 4;
      return toPercentageStr(roundToFixedN(n, v));
    }
  },
  percentage: {
    formatter: v => roundToFixedN(v > 100 ? 2 : 4, v)
  },
  currency_formula: {
    formatter: v =>
      (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  },
  hours_from: {
    formatter: v => (v || 0).toLocaleString('en-US')
  },
  standard_event: {
    formatter: v => (v || 0).toLocaleString('en-US')
  }
};

/**
 * Default metadata based on column key
 */
const _KEY_METADATA: Object = {
  revenue: {
    formatter: v =>
      (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  },
  revenue_blocked: {
    formatter: v =>
      (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  },
  cost: {
    formatter: v =>
      (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  },
  /*
   * eo_click and eo_impression have both React Components and numbers as items. We must therefore override the text alignment at the column level.
   */
  eo_click: {
    style: { textAlign: 'right' }
  },
  eo_impression: {
    style: { textAlign: 'right' }
  },
  /*
   * the tilde_tags column may have items that are both fully numeric or fully text. We must therefore set the text alignment at the column level.
   */
  last_attributed_touch_data_tilde_tags: {
    style: { textAlign: 'left' }
  }
};

const _FORMULAS: Object = {
  fraud_codes: [
    {
      fn: 'concat',
      fields: ['fraud_codes'],
      // as long as type isn't defaulted to "formula",
      // the dimensions in fields won't be requested
      // as extra datasources
      type: 'dimension_derived',
      key: 'fraud_codes_description'
    }
  ],
  eo_install: [
    {
      fn: '/',
      fields: ['eo_install', 'eo_click'],
      key: 'eo_click_to_eo_install'
    }
  ],
  eo_reinstall: [
    {
      fn: '/',
      fields: ['eo_reinstall', 'eo_click'],
      key: 'eo_click_to_eo_reinstall'
    }
  ],
  eo_open: [
    { fn: '/', fields: ['eo_open', 'eo_click'], key: 'eo_click_to_eo_open' }
  ],
  eo_web_session_start: [
    {
      fn: '/',
      fields: ['eo_web_session_start', 'eo_click'],
      key: 'eo_click_to_eo_web_session_start'
    }
  ],
  eo_branch_cta_view: [
    {
      fn: '/',
      fields: ['eo_click', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_click'
    },
    {
      fn: '/',
      fields: ['eo_install', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_install'
    },
    {
      fn: '/',
      fields: ['eo_open', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_open'
    },
    {
      fn: '/',
      fields: ['eo_dismissal', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_dismissal'
    }
  ].concat(
    Object.keys(COMMERCE_EVENT_NAMES)
      .filter(e => e !== 'revenue')
      .map(e => {
        return {
          fn: '/',
          fields: [e, 'eo_branch_cta_view'],
          key: `eo_branch_cta_view_to_${e}`
        };
      })
  ),
  eo_install_blocked: [
    {
      key: 'eo_install_blocked_to_eo_install_total',
      fn: '/',
      fields: [
        'eo_install_blocked',
        { fn: '+', fields: ['eo_install', 'eo_install_blocked'] }
      ]
    },
    {
      key: 'eo_install_plus_blocked',
      type: 'data_sources',
      fn: '+',
      fields: ['eo_install', 'eo_install_blocked']
    }
  ],
  eo_click_blocked: [
    {
      key: 'eo_click_blocked_to_eo_click_total',
      fn: '/',
      fields: [
        'eo_click_blocked',
        {
          fn: '+',
          fields: ['eo_click', 'eo_click_blocked']
        }
      ]
    },
    {
      key: 'eo_click_plus_blocked',
      type: 'data_sources',
      fn: '+',
      fields: ['eo_click', 'eo_click_blocked']
    }
  ],
  eo_open_blocked: [
    {
      key: 'eo_open_blocked_to_eo_open_total',
      fn: '/',
      fields: [
        'eo_open_blocked',
        {
          fn: '+',
          fields: ['eo_open', 'eo_open_blocked']
        }
      ]
    },
    {
      key: 'eo_open_plus_blocked',
      type: 'data_sources',
      fn: '+',
      fields: ['eo_open', 'eo_open_blocked']
    }
  ],
  eo_reinstall_blocked: [
    {
      key: 'eo_reinstall_blocked_to_eo_reinstall_total',
      fn: '/',
      fields: [
        'eo_reinstall_blocked',
        { fn: '+', fields: ['eo_reinstall', 'eo_reinstall_blocked'] }
      ]
    },
    {
      key: 'eo_reinstall_plus_blocked',
      type: 'data_sources',
      fn: '+',
      fields: ['eo_reinstall', 'eo_reinstall_blocked']
    }
  ],
  eo_impression_blocked: [
    {
      key: 'eo_impression_blocked_to_eo_impression_total',
      fn: '/',
      fields: [
        'eo_impression_blocked',
        { fn: '+', fields: ['eo_impression', 'eo_impression_blocked'] }
      ]
    },
    {
      key: 'eo_impression_plus_blocked',
      type: 'data_sources',
      fn: '+',
      fields: ['eo_impression', 'eo_impression_blocked']
    }
  ],
  cost: [
    {
      fn: '/',
      fields: ['cost', 'eo_install'],
      key: 'eo_install_to_cost',
      type: 'currency_formula'
    },
    {
      fn: '/',
      fields: ['cost', 'eo_click'],
      key: 'eo_click_to_cost',
      type: 'currency_formula'
    },
    { fn: '/', fields: ['revenue', 'cost'], key: 'ROAS' },
    {
      fn: '/',
      fields: ['cost', { fn: '/', fields: ['eo_impression', 1000] }],
      key: 'eo_impression_to_cost',
      type: 'currency_formula'
    },
    {
      key: 'ROI',
      fn: '/',
      fields: [{ fn: '-', fields: ['revenue', 'cost'] }, 'cost']
    },
    {
      fn: '/',
      fields: ['cost', 'PURCHASE'],
      key: 'PURCHASE_to_cost',
      type: 'currency_formula'
    }
  ]
};

export const _POPOVERS: Object = {
  cost: {
    title:
      'Total cost (spend) for those dimensions (analytics tags, user data, time range), regardless of cost model'
  },
  eo_install_to_cost: {
    title: 'Effective Cost Per Install: cost divided by number of installs'
  },
  eo_click_to_cost: {
    title: 'Effective Cost Per Click: cost divided by number of clicks'
  },
  eo_impression_to_cost: {
    title:
      'Effective Cost Per Milli: cost divided by impressions divided by 1000'
  },
  PURCHASE_to_cost: {
    title:
      'Effective Cost Per Action: cost divided by purchases [includes web and app purchases]'
  },
  ROI: {
    title:
      'Return On Investment: profit (revenue minus cost) divided by cost, as a percentage'
  },
  ROAS: {
    title: 'Return On Ad Spend: revenue divided by cost, as a percentage'
  },
  eo_install_hours_from_last_attributed_touch_to_event: {
    title:
      'This shows the average number of hours from a click occurring to an install being recorded. This should be used for fraud detection only - measuring which secondary publishers have an irregularly high average time for click to install. This metric is capped at 24 hours per event, making it useful primarily for fraud detection.'
  },
  eo_reinstall_hours_from_last_attributed_touch_to_event: {
    title:
      'This shows the average number of hours from a click occurring to a reinstall being recorded. This should be used for fraud detection only - measuring which secondary publishers have an irregularly high average time for click to install. This metric is capped at 24 hours per event, making it useful primarily for fraud detection.'
  },
  eo_install_plus_blocked: {
    title: 'Blocked and unblocked installs'
  },
  eo_install_blocked: {
    title: 'Installs blocked based on active fraud rules'
  },
  eo_install_blocked_to_eo_install_total: {
    title: 'Percentage of blocked installs over total installs'
  },
  eo_click_plus_blocked: {
    title: 'Blocked and unblocked clicks'
  },
  eo_click_blocked: {
    title: 'Clicks blocked based on active fraud rules'
  },
  eo_click_blocked_to_eo_click_total: {
    title: 'Percentage of blocked clicks over total clicks'
  },
  eo_open_plus_blocked: {
    title: 'Blocked and unblocked opens'
  },
  eo_open_blocked: {
    title: 'Opens blocked based on active fraud rules'
  },
  eo_open_blocked_to_eo_open_total: {
    title: 'Percentage of blocked opens over total opens'
  },
  eo_reinstall_plus_blocked: {
    title: 'Blocked and unblocked reinstalls'
  },
  eo_reinstall_blocked: {
    title: 'Reinstalls blocked based on active fraud rules'
  },
  eo_reinstall_blocked_to_eo_reinstall_total: {
    title: 'Percentage of blocked reinstalls over total reinstalls'
  },
  eo_impression_plus_blocked: {
    title: 'Blocked and unblocked impressions'
  },
  eo_impression_blocked: {
    title: 'Impressions blocked based on active fraud rules'
  },
  eo_impression_blocked_to_eo_impression_total: {
    title: 'Percentage of blocked impressions over total impressions'
  },
  eo_install_seconds_from_last_attributed_touch_to_event_under_10: {
    title:
      'The percentage of unblocked installs where the time from click to install was under 10 seconds - an indicator of click injection'
  },
  eo_install_seconds_from_last_attributed_touch_to_event_between_10_and_30: {
    title:
      'The percentage of unblocked installs where the time from click to install was between 10 and 30 seconds'
  },
  eo_install_seconds_from_last_attributed_touch_to_event_over_30: {
    title:
      'The percentage of unblocked installs where the time from click to install was over 30 seconds'
  },
  eo_install_days_from_last_attributed_touch_to_event_under_1: {
    title:
      'The percentage of unblocked installs where the time from click to install was under 1 day'
  },
  eo_install_days_from_last_attributed_touch_to_event_over_1: {
    title:
      'The percentage of unblocked installs where the time from click to install was over 1 day - an indicator of click flooding'
  },

  // start of Journeys
  eo_click_to_eo_install: {
    title: 'The percentage of attributed installs divided by clicks'
  },
  eo_click_to_eo_reinstall: {
    title: 'The percentage of attributed reinstalls divided by clicks'
  },
  eo_click_to_eo_open: {
    title: 'The percentage of attributed opens divided by clicks'
  },
  eo_click_to_eo_web_session_start: {
    title: 'The percentage of attributed web session starts divided by clicks'
  },
  eo_branch_cta_view_to_eo_click: {
    title:
      'The percentage of clicks attributed to Journeys divided by Journeys views'
  },
  eo_branch_cta_view_to_eo_install: {
    title:
      'The percentage of installs attributed to Journeys divided by Journeys views'
  },
  eo_branch_cta_view_to_eo_open: {
    title:
      'The percentage of opens attributed to Journeys divided by Journeys views'
  },
  eo_branch_cta_view_to_eo_dismissal: {
    title:
      'The percentage of dismissals attributed to Journey divided by Journeys views'
  },
  eo_branch_cta_view_to_PURCHASE: {
    title:
      'The percentage of purchase events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_ADD_TO_CART: {
    title:
      'The percentage of add to cart events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_ADD_TO_WISHLIST: {
    title:
      'The percentage of add to wishlist events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_VIEW_CART: {
    title:
      'The percentage of view cart events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_INITIATE_PURCHASE: {
    title:
      'The percentage of initiate purchase events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_ADD_PAYMENT_INFO: {
    title:
      'The percentage of add payment info events attributed to Journeys, divided by Journeys views'
  },
  eo_branch_cta_view_to_SPEND_CREDITS: {
    title:
      'The percentage of spend credit events attributed to Journeys, divided by Journeys views'
  },
  last_attributed_touch_data_tilde_journey_name: {
    title: "'Campaign Name' or 'title' for an audience rule / Journeys banner"
  },
  last_attributed_touch_data_tilde_journey_id: {
    title: 'Audience rule id for a Journeys banner'
  },
  last_attributed_touch_data_tilde_view_name: {
    title: "'View Name' or 'title' for a Branch view / template"
  },
  last_attributed_touch_data_tilde_view_id: {
    title: 'Branch view id for a Journeys banner'
  },
  last_attributed_touch_data_tilde_agency_id: {
    title: 'Agency id'
  },
  last_attributed_touch_data_tilde_agency: {
    title: 'Agency name'
  },

  // start of basic definitions
  eo_click: {
    title:
      'Number of times users tapped on a Branch link, or on an ad where Branch is able to fetch click data from a third party (e.g. Facebook ads)'
  },
  eo_install: {
    title:
      'Installs attributed to this feature – installs are the first app open after downloading the app'
  },
  eo_reinstall: {
    title: 'Reinstalls attributed to your selected filters'
  },
  eo_open: {
    title: 'App opens attributed to your selected filters'
  },
  eo_web_session_start: {
    title:
      'Web sessions attributed to your selected filters – triggered when a new tab for a website is opened, or when a Branch link is clicked that leads to the website'
  },
  eo_branch_cta_view: {
    title: 'Views of a Journeys banner or interstitial'
  },
  eo_sms_sent: {
    title:
      'Number of times users texted themselves an app download link from desktop web'
  },
  eo_web_to_app_auto_redirect: {
    title:
      'Number of times users were auto-redirected to the app without clicking, upon landing on a web page'
  },
  eo_impression: {
    title: 'Ad impressions'
  },
  eo_pageview: {
    title: 'Attributed pageviews'
  },
  eo_dismissal: {
    title: 'Journey Dismissals'
  },

  // start of analytic labels
  last_attributed_touch_data_tilde_feature: {
    title:
      'Feature of last touch – this can be set as a custom analytics tag or can be prefilled with the Branch feature, e.g. Journeys'
  },
  last_attributed_touch_data_tilde_channel: {
    title: 'Channel of last touch – this can be set as a custom analytics tag'
  },
  last_attributed_touch_data_tilde_campaign: {
    title:
      'Campaign of last touch – this can be set as a custom analytics tag, or set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_stage: {
    title: 'Stage of last touch – this can be set as a custom analytics tag'
  },
  last_attributed_touch_data_tilde_tags: {
    title: 'Tags of last touch – this can be set as a custom analytics tag'
  },

  // start of commerce events
  PURCHASE: {
    title: 'Purchases attributed to your selected filters'
  },
  revenue: {
    title: 'Revenue attributed to your selected filters'
  },

  // start of v2 events
  ADD_TO_CART: {
    title: 'Add to cart events attributed to your selected filters'
  },
  ADD_TO_WISHLIST: {
    title: 'Add to wishlist events attributed to your selected filters'
  },
  VIEW_CART: {
    title: 'View cart events attributed to your selected filters'
  },
  INITIATE_PURCHASE: {
    title: 'Initiate purchase events attributed to your selected filters'
  },
  ADD_PAYMENT_INFO: {
    title: 'Add payment info events attributed to your selected filters'
  },
  SPEND_CREDITS: {
    title: 'Spend credits events attributed to your selected filters'
  },
  RESERVE: {
    title: 'Reserve events attributed to your selected filters'
  },
  SEARCH: {
    title: 'Search events attributed to your selected filters'
  },
  CLICK_AD: {
    title: 'Click ad events attributed to your selected filters'
  },
  VIEW_AD: {
    title: 'View ad events attributed to your selected filters'
  },
  VIEW_ITEM: {
    title:
      'View item events attributed to your selected filters – usually on a screen that can only support showing one item'
  },
  VIEW_ITEMS: {
    title:
      'View items events attributed to your selected filters – usually on a screen that can support showing multiple items, such as a list view'
  },
  RATE: {
    title: 'Rate events attributed to your selected filters'
  },
  SHARE: {
    title: 'Share events attributed to your selected filters'
  },
  COMPLETE_REGISTRATION: {
    title: 'Complete registration events attributed to your selected filters'
  },
  COMPLETE_TUTORIAL: {
    title: 'Complete tutorial events attributed to your selected filters'
  },
  ACHIEVE_LEVEL: {
    title: 'Achieve level events attributed to your selected filters'
  },
  UNLOCK_ACHIEVEMENT: {
    title: 'Unlock achievement events attributed to your selected filters'
  },
  INVITE: {
    title: 'Invite events attributed to your selected filters'
  },
  LOGIN: {
    title: 'Login events attributed to your selected filters'
  },
  SUBSCRIBE: {
    title: 'Subscribe events attributed to your selected filters'
  },
  START_TRIAL: {
    title: 'Start trial events attributed to your selected filters'
  },

  // start of blocked commerce events
  PURCHASE_BLOCKED: {
    title: 'Blocked purchases attributed to your selected filters'
  },
  revenue_blocked: {
    title: 'Blocked revenue attributed to your selected filters'
  },

  // start of blocked v2 events
  ADD_TO_CART_BLOCKED: {
    title: 'Blocked add to cart events attributed to your selected filters'
  },
  ADD_TO_WISHLIST_BLOCKED: {
    title: 'Blocked add to wishlist events attributed to your selected filters'
  },
  VIEW_CART_BLOCKED: {
    title: 'Blocked view cart events attributed to your selected filters'
  },
  INITIATE_PURCHASE_BLOCKED: {
    title:
      'Blocked initiate purchase events attributed to your selected filters'
  },
  ADD_PAYMENT_INFO_BLOCKED: {
    title: 'Blocked add payment info events attributed to your selected filters'
  },
  SPEND_CREDITS_BLOCKED: {
    title: 'Blocked spend credits events attributed to your selected filters'
  },
  RESERVE_BLOCKED: {
    title: 'Blocked reserve events attributed to your selected filters'
  },
  SEARCH_BLOCKED: {
    title: 'Blocked search events attributed to your selected filters'
  },
  CLICK_AD_BLOCKED: {
    title: 'Blocked click ad events attributed to your selected filters'
  },
  VIEW_AD_BLOCKED: {
    title: 'Blocked view ad events attributed to your selected filters'
  },
  VIEW_ITEM_BLOCKED: {
    title:
      'Blocked view item events attributed to your selected filters – usually on a screen that can only support showing one item'
  },
  VIEW_ITEMS_BLOCKED: {
    title:
      'Blocked view items events attributed to your selected filters – usually on a screen that can support showing multiple items, such as a list view'
  },
  RATE_BLOCKED: {
    title: 'Blocked rate events attributed to your selected filters'
  },
  SHARE_BLOCKED: {
    title: 'Blocked share events attributed to your selected filters'
  },
  COMPLETE_REGISTRATION_BLOCKED: {
    title:
      'Blocked complete registration events attributed to your selected filters'
  },
  COMPLETE_TUTORIAL_BLOCKED: {
    title:
      'Blocked complete tutorial events attributed to your selected filters'
  },
  ACHIEVE_LEVEL_BLOCKED: {
    title: 'Blocked achieve level events attributed to your selected filters'
  },
  UNLOCK_ACHIEVEMENT_BLOCKED: {
    title:
      'Blocked unlock achievement events attributed to your selected filters'
  },
  INVITE_BLOCKED: {
    title: 'Blocked invite events attributed to your selected filters'
  },
  LOGIN_BLOCKED: {
    title: 'Blocked login events attributed to your selected filters'
  },
  SUBSCRIBE_BLOCKED: {
    title: 'Blocked subscribe events attributed to your selected filters'
  },
  START_TRIAL_BLOCKED: {
    title: 'Blocked start trial events attributed to your selected filters'
  },

  // extras
  attributed: {
    title:
      'Whether the event was attributed to any touch (click or impression) as opposed to unattributed (attributed: false). Unattributed data is the same as "organic" or "direct" data.'
  },
  user_data_platform: {
    title:
      'Platform (web vs app, desktop vs mobile) on which the event took place'
  },
  user_data_environment: {
    title:
      'Environment where the event occurred, which can distinguish between e.g. full app vs instant app'
  },
  user_data_geo_country_code: {
    title: 'Country, derived from IP address'
  },
  user_data_os: {
    title: 'Operating system'
  },
  user_data_environment: {
    title: 'Environment'
  },
  last_attributed_touch_data_tilde_advertising_partner_name: {
    title:
      'Ad partner of last touch – this is set automatically to be the name of the ad partner'
  },
  last_attributed_touch_data_dollar_3p: {
    title:
      'Ad partner (3p) of last touch – this is set automatically to be the 3p value of the ad partner'
  },
  last_attributed_touch_data_tilde_secondary_publisher: {
    title:
      'Secondary publisher of last touch – this is set automatically to include information about the publisher where the ad was served'
  },
  last_attributed_touch_data_tilde_ad_set_name: {
    title:
      'Ad set name of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_ad_name: {
    title:
      'Ad name of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_creative_name: {
    title:
      'Creative name of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_type: {
    title:
      'Whether the last touch was an impression, click, or web to app auto redirect'
  },
  last_attributed_touch_data_tilde_keyword: {
    title: 'Keyword of last touch'
  },
  fraud_names: {
    title: 'The name of the fraud rule responsible for blocking an event'
  },
  fraud_codes: {
    title: 'The code (ID) of the fraud rule responsible for blocking an event'
  },

  // liveview
  name: {
    title: 'Event name'
  },
  timestamp: {
    title: 'Event timestamp'
  },
  origin: {
    title:
      'Whether the event originated with a Branch SDK/API call, or was sent by Segment or mParticle'
  },
  last_attributed_touch_type: {
    title:
      'Whether the last touch was an impression, click, or web to app auto redirect'
  },
  last_attributed_touch_timestamp: {
    title: 'Unix timestamp in milliseconds for the last attributed touch'
  },
  deep_linked: {
    title:
      'True if the current app or web session resulted in the user being deep linked'
  },
  user_data_aaid: {
    title:
      'Android/Google advertising id of the device where the event occurred'
  },
  user_data_idfa: {
    title: 'iOS identifier for advertising – shared across all apps'
  },
  user_data_idfv: {
    title: 'iOS identifier for vendor – scoped to vendor'
  },
  user_data_android_id: {
    title: 'Android hardware id'
  },
  id: {
    title: 'Event id'
  },
  last_attributed_touch_data_tilde_id: {
    title: 'id for the last attributed touch (Quick Links only)'
  },
  last_attributed_touch_data_tilde_campaign_id: {
    title:
      'Campaign id of last touch – this is set automatically by ad networks like Facebook, or can be set with analytics tags on a link'
  },
  last_attributed_touch_data_tilde_creative_id: {
    title:
      'Creative id of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_ad_set_id: {
    title:
      'Ad set id of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_ad_id: {
    title:
      'Ad id of last touch – this is set automatically by ad networks like Facebook'
  },
  last_attributed_touch_data_tilde_branch_ad_format: {
    title:
      'Ad format of the last touch – chosen upon Branch link creation and set as an analytics tag (e.g. Search, Display, Product Ad, App only)'
  },
  last_attributed_touch_data_tilde_technology_partner: {
    title:
      'Technology partner of last touch, as set with an analytics tag – any third party that is used to enhance a campaign (e.g. audiences being provided, creative optimization), with the exception of agencies'
  },
  last_attributed_touch_data_tilde_banner_dimensions: {
    title:
      'Dimensions of display banner of the last touch, as set with an analytics tag'
  },
  last_attributed_touch_data_tilde_placement: {
    title:
      'Placement of the last touch, as set with an analytics tag – actual app or website the ad appears on display campaigns'
  },
  last_attributed_touch_data_tilde_keyword_id: {
    title: 'Unique id for keyword of the last touch as provided by Google'
  },
  last_attributed_touch_data_tilde_agency: {
    title:
      'Agency of the last touch, as set with an analytics tag – agency that runs ad campaigns on behalf of advertiser'
  },
  last_attributed_touch_data_tilde_optimization_model: {
    title:
      'Optimization model of the last touch, as set with an analytics tag – model detailing the event for which the campaign is optimizing (e.g. for installs, clicks, events)'
  },
  last_attributed_touch_data_tilde_secondary_ad_format: {
    title:
      'Secondary ad format of the last touch, as set with an analytics tag or during ad link creation flow in the dashboard – Ad Partner Specific Ad format, such as Display Engagement Ad for Google'
  },
  // TODO add popovers
  // last_attributed_touch_data_tilde_secondary_publisher_id: {},
  // last_attributed_touch_data_tilde_customer_secondary_publisher: {},
  // last_attributed_touch_data_tilde_customer_ad_set_name: {},
  // last_attributed_touch_data_tilde_customer_ad_name: {},
  // last_attributed_touch_data_tilde_customer_keyword: {},
  // last_attributed_touch_data_tilde_customer_placement: {},
  // last_attributed_touch_data_tilde_sub_site_name: {},
  // last_attributed_touch_data_tilde_customer_sub_site_name: {},
  // last_attributed_touch_data_tilde_customer_campaign: {},
  // customer_event_alias: {},
  referring_domain: {
    title:
      'Domain of the URL that the user visited before landing on a page with a Journeys – this should use the same data source that we use for the "came directly from a URL" audience filter'
  },
  current_feature: {
    title: 'Branch feature (determined by Branch) of the current touch'
  },
  via_features: {
    title:
      'All Branch features (determined by Branch) which were determined, via attribution, to have led to this event'
  },
  third_party_provider: {
    title:
      'Advertising partner short-code (e.g. e_rs) specified for the last attributed touch'
  },
  web_format: {
    title:
      'Set if an event originated with a touch on Google AMP or Facebook Instant Article'
  },
  last_attributed_touch_data: {
    title:
      'For touches, this includes all touch data, including analytics tags, redirection information, and custom key-value pairs – for all other events, this includes the same data, but from the touch to which this event is attributed'
  },
  user_data_os_version: {
    title: 'Operating system version of the device where the event occurred'
  },
  user_data_limit_ad_tracking: {
    title: 'Whether limit ad tracking was set to true to the event'
  },
  user_data_user_agent: {
    title:
      'User agent of the browser or app where the event occurred – usually associated with a webview'
  },
  user_data_ip: {
    title: 'IP address'
  },
  user_data_developer_identity: {
    title: 'Developer-specified identity for a user'
  },
  user_data_language: {
    title:
      'Language specified in the user_agent string (or in some cases other headers)'
  },
  user_data_brand: {
    title: 'Brand of the device where the event occurred'
  },
  user_data_device_type: {
    title: 'Device type of the device where the event occurred'
  },
  user_data_model: {
    title: 'Model of the device where the event occurred'
  },
  user_data_sdk_version: {
    title: 'Branch SDK version used to track this event'
  },
  user_data_app_version: {
    title: 'App version where the event occurred'
  },
  user_data_model: {
    title: 'Model of the device where the event occurred'
  },
  user_data_geo_dma_code: {
    title: 'DMA code, derived from IP address'
  },
  transaction_id: {
    title: 'Partner-specified transaction id for their internal use'
  },
  revenue_in_usd: {
    title: 'Revenue in USD, based off of revenue, currency, and exchange rate'
  },
  exchange_rate: {
    title:
      'Exchange rate from the time the event occurred (may be up to 24 hours old but is typically updated within the hour)'
  },
  currency: {
    title:
      'Currency that revenue, price, shipping, tax were orginally reported in by the partner'
  },
  shipping: {
    title: 'Shipping cost associated with the transaction'
  },
  tax: {
    title: 'Total tax associated with the transaction'
  },
  coupon: {
    title:
      'Transaction coupon redeemed with the transaction (e.g. "SPRING2017")'
  },
  affiliation: {
    title:
      'Store or affiliation from which this transaction occurred (e.g. Google Store)'
  },
  search_query: {
    title: 'Search query associated with the event'
  },
  event_description: {
    title:
      'Description associated with the event, not necessarily specific to any individual content items'
  },
  custom_data: {
    title: 'Custom data associated with the event'
  },
  content_items: {
    title: 'Content items associated with the event'
  },
  dismissal_source: {
    title: 'Journey dismissal source'
  }
};

const _GROUPS: Object = {
  eo_install_seconds_from_last_attributed_touch_to_event_under_10: {
    key: 'short_cti_times'
  },
  eo_install_seconds_from_last_attributed_touch_to_event_between_10_and_30: {
    key: 'short_cti_times'
  },
  eo_install_seconds_from_last_attributed_touch_to_event_over_30: {
    key: 'short_cti_times'
  },
  eo_install_days_from_last_attributed_touch_to_event_under_1: {
    key: 'long_cti_times'
  },
  eo_install_days_from_last_attributed_touch_to_event_over_1: {
    key: 'long_cti_times'
  },
  eo_install_blocked: {
    key: 'blocked_install_group'
  },
  eo_install_blocked_to_eo_install_total: {
    key: 'blocked_install_group'
  },
  eo_click_blocked: {
    key: 'blocked_click_group'
  },
  eo_click_blocked_to_eo_click_total: {
    key: 'blocked_click_group'
  },
  eo_open_blocked: {
    key: 'blocked_open_group'
  },
  eo_open_blocked_to_eo_open_total: {
    key: 'blocked_open_group'
  },
  eo_reinstall_blocked: {
    key: 'blocked_reinstall_group'
  },
  eo_reinstall_blocked_to_eo_reinstall_total: {
    key: 'blocked_reinstall_group'
  },
  eo_impression_blocked: {
    key: 'blocked_impression_group'
  },
  eo_impression_blocked_to_eo_impression_total: {
    key: 'blocked_impression_group'
  },
  fraud_codes: {
    key: 'fraud_codes_group'
  },
  fraud_codes_description: {
    key: 'fraud_codes_group'
  }
};

const _COLUMN_HEADER_STYLE = {
  eo_install_blocked: 'h_jc-c',
  eo_install_blocked_to_eo_install_total: 'h_jc-c',
  eo_click_blocked: 'h_jc-c',
  eo_click_blocked_to_eo_click_total: 'h_jc-c',
  eo_open_blocked: 'h_jc-c',
  eo_open_blocked_to_eo_open_total: 'h_jc-c',
  eo_reinstall_blocked: 'h_jc-c',
  eo_reinstall_blocked_to_eo_reinstall_total: 'h_jc-c',
  eo_impression_blocked: 'h_jc-c',
  eo_impression_blocked_to_eo_impression_total: 'h_jc-c'
};

const _GRADIENT_COLUMNS = [
  'eo_install_seconds_from_last_attributed_touch_to_event_under_10',
  'eo_install_days_from_last_attributed_touch_to_event_over_1',
  'eo_install_blocked_to_eo_install_total',
  'eo_click_blocked_to_eo_click_total',
  'eo_open_blocked_to_eo_open_total',
  'eo_reinstall_blocked_to_eo_reinstall_total',
  'eo_impression_blocked_to_eo_impression_total'
];

export const CUSTOM_EVENT_PREFIX = 'eo_custom_event-';
export const BLOCKED_CUSTOM_EVENT_PREFIX = 'eo_custom_event_blocked-';

export function isCustomEvent(event: string): boolean {
  return event.indexOf(CUSTOM_EVENT_PREFIX) > -1;
}

export function isBlockedCustomEvent(event: string): boolean {
  return event.indexOf(BLOCKED_CUSTOM_EVENT_PREFIX) > -1;
}

export function getCustomEventName(key: string): string {
  if (isCustomEvent(key)) {
    return key.slice(CUSTOM_EVENT_PREFIX.length);
  }
  if (isBlockedCustomEvent(key)) {
    return 'blocked ' + key.slice(BLOCKED_CUSTOM_EVENT_PREFIX.length);
  }
  return key;
}

/**
 * Recursively resolve all data sources needed for a formula
 * Treat revenue as a special data source and map it to `eo_commerce_event`
 * @param  {Array<string>} sources
 * @return {Array<string>}
 */
function getDataSourcesForFormula(sources) {
  if (!sources) {
    return [];
  }
  return sources.flatMap(source => {
    if (typeof source === 'string') {
      return ALL_EVENT_NAMES[source] ? ALL_EVENT_NAMES[source] : source;
    }
    return getDataSourcesForFormula(source.fields);
  });
}

/**
 * Fetch the data source/s corresponding to the given type of event
 * @param  {String} type
 * @param  {Array<String>} sources
 * @param  {string} key
 * @return {Array<String>}  return a list of data sources corresponding to
 * the given type
 */
export function getUnderlyingDataSources(
  type: string,
  sources?: any,
  key: string
): ?Array<*> {
  switch (type) {
    case 'data_sources':
      return [key];
    case 'standard_event':
      return sources && [sources[key]];
    case 'eo_custom_event':
    case 'eo_custom_event_blocked':
      return [type];
    case 'formula':
    case 'currency_formula':
      return getDataSourcesForFormula(sources);
    default:
      return null;
  }
}

/**
 * Used to add additional columns or metadata to a column based on the contents of the column
 * NOTE: this context contains info about which columns to show calculate formulas for
 * we can add more info to this context when needed
 this = {
    formulas: ['eo_install', 'eo_reinstall']
 }
 *
 */
export function getColumnMetadata(
  type: string,
  sources?: any,
  key: string
): Object {
  // set column metadata based on column type
  let metadata = _TYPE_METADATA[type];

  // set column metadata based on column key. Overwrites type metadata
  if (_KEY_METADATA[key]) {
    metadata = R.merge(metadata, _KEY_METADATA[key]);
  }

  const formulas = R.path(['formulas'], this);
  // if no formulas list included in context, we will assume you want all formulas to show
  if (!formulas || (R.contains(key, formulas) && _FORMULAS[key])) {
    metadata = R.assoc('formulas', _FORMULAS[key], metadata);
  }

  if (_POPOVERS[key]) {
    metadata = R.assoc('popover', _POPOVERS[key], metadata);
  }

  if (_GROUPS[key]) {
    metadata = R.assoc('group', _GROUPS[key], metadata);
  }

  if (_COLUMN_HEADER_STYLE[key]) {
    metadata = R.assoc('className', _COLUMN_HEADER_STYLE[key], metadata);
  }

  if (R.contains(key, _GRADIENT_COLUMNS)) {
    metadata = R.assoc('gradient', true, metadata);
  }

  const dataSources = getUnderlyingDataSources(type, sources, key);
  if (dataSources) {
    metadata = R.assoc('dataSources', dataSources, metadata);
  }

  const display = DRUID_TO_HUMAN_READABLE[key] || getCustomEventName(key);

  return {
    ...metadata,
    type,
    key,
    display
  };
}

/*
 * Helper function applying an operator over a list of results
 */
export function calculate(fn: string, results: number[]): number {
  switch (fn) {
    case '+':
      return results.reduce((acc, curr) => acc + curr, 0);
    case '-':
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }
        return !curr ? curr : acc - curr;
      }, 0);
    case '*':
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }
        return !curr ? curr : acc * curr;
      }, 0);
    case '/':
    default:
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }
        return !curr ? curr : acc / curr;
      }, 0);
  }
}

/**
 * Generates a formula used to concatenate fields as a growing string
 */
export function generateConcatFormula(
  fields: Array<string | number | Object>
): (row: Object) => any {
  return function(row) {
    return fields.reduce((acc, curr) => {
      switch (typeof curr) {
        case 'string':
          return acc + row[curr];
        default:
          return acc;
      }
    }, '');
  };
}

/**
 * Generates a formula used to calculate a derived column value such as "click to install percentage"
 */
export function generateFormula(
  fn: string,
  fields: Array<string | number | Object>
): (row: Object) => any {
  return function(row) {
    function recurse(fn1, fields1) {
      const results = fields1.map(f => {
        switch (typeof f) {
          case 'object':
            return recurse(f.fn, f.fields);
          case 'number':
            return f;
          case 'string':
            return row[f];
          default:
            return 0;
        }
      }, []);

      return calculate(fn1, results);
    }
    return recurse(fn, fields);
  };
}

function _getFormulaColumnMetadata(parts: {
  key: string,
  type?: string,
  fn: string,
  fields: Array<string | number | Object>
}): Object {
  const { key, type, fn, fields } = parts;

  const metadata = getColumnMetadata(type || 'formula', fields, key);

  if (fn === 'concat') {
    return R.merge(metadata, { getValue: generateConcatFormula(fields) });
  }

  return R.merge(metadata, { getValue: generateFormula(fn, fields) });
}

export function injectColumnFormulaFromMetadata(columns: Columns): Columns {
  return R.pipe(
    R.map(column => {
      if (!column.formulas) {
        return column;
      }

      return [column, ...column.formulas.map(_getFormulaColumnMetadata)];
    }),
    R.flatten
  )(columns);
}

export const getRowValueHash = (row: Object, dimensions: Array<string>) => {
  return dimensions.reduce((hash, dimension) => {
    const value = row[dimension];
    if (!hash) {
      return `${value}`;
    }
    return `${hash}-${value}`;
  }, '');
};

export const generateRowHashMap = (
  rows: Object[],
  dimensions: Array<string>
) => {
  return rows.reduce((hashMap, row) => {
    const key = getRowValueHash(row, dimensions);
    return R.assoc(key, row, hashMap);
  }, {});
};

export const isAppPlatform = R.contains(R.__, [
  'IOS_APP',
  'ANDROID_APP',
  'DESKTOP_APP',
  'TV_APP'
]);
export const WEB_COPY_KEYS = ['eo_click', 'eo_impression'];

export function copyWebClicksToAppClicks(dimensions: string[], data: Object[]) {
  const PLATFORMS = {
    IOS_APP: 'IOS_WEB',
    ANDROID_APP: 'ANDROID_WEB',
    DESKTOP_APP: 'DESKTOP_WEB',
    TV_APP: 'TV_WEB'
  };

  const rowHashMap = generateRowHashMap(data, dimensions);

  return data.map(row => {
    const refPlatform = PLATFORMS[row.user_data_platform];
    // if there is no corresponding lookup platform i.e. DESKTOP_WEB, return early
    if (!refPlatform) {
      return row;
    }

    // find the corresponding reference row w/ matching dimensions
    const rowKey = getRowValueHash(
      R.assoc('user_data_platform', refPlatform, row),
      dimensions
    );
    const refRow = rowHashMap[rowKey];

    // early exit if no corresponding reference row
    if (!refRow) {
      return row;
    }

    // mutate the copy values for app platform from web platform
    return R.merge(row, R.pick(WEB_COPY_KEYS, refRow));
  });
}

export function copyWebClicksToAppTotalRowFilter(row: Row) {
  if (!isAppPlatform(row.user_data_platform)) {
    return row;
  }
  return R.omit(WEB_COPY_KEYS, row);
}

/* DANA-780 HELPERS END */

// EventType as defined in app-service:
// https://github.com/BranchMetrics/app-service/blob/master/app-application/resources/app.proto#L40
export function formatEoDatasource(eoDatasource: string): string {
  if (!eoDatasource) {
    return '';
  }

  // remove eo_
  const eventType = eoDatasource.slice(3);

  return eventType.toUpperCase();
}

export function getEventDatasource(event: string): string {
  const eoDatasource = getEoDatasource(event);
  return formatEoDatasource(eoDatasource);
}

export function getEoDatasource(event: string): string {
  if (isCustomEvent(event)) {
    return EO_CUSTOM_EVENT;
  }
  return ALL_EVENT_NAMES[event];
}

export function mapToEventName(name: string): string {
  if (name.startsWith(EO_CUSTOM_EVENT)) {
    return name.replace(`${EO_CUSTOM_EVENT}-`, '');
  }

  return name;
}

/**
 * Convert from human readable value to appropriate druid query value
 */
export function convertHumanReadableToDruid(humanReadable: string) {
  return Object.keys(DRUID_TO_HUMAN_READABLE).find(key => {
    return DRUID_TO_HUMAN_READABLE[key] === humanReadable;
  });
}

const LAST_ATTRIBUTED_TOUCH_TYPES = ['eo_click', 'eo_impression'];

// Warning on last_attributed_touch_type temporarily removed until EG-1116.
// https://branch.atlassian.net/browse/EG-2539
const DIMENSION_BLACK_LIST = [
  // 'last_attributed_touch_type',
  'first_event_for_user'
];

export function generateLastTouchWarningText(
  filter: Object,
  columnKeys: Object
) {
  const touchTypes = columnKeys.filter(key =>
    R.contains(key, LAST_ATTRIBUTED_TOUCH_TYPES)
  );
  const dimensionsToValues = getListOfDimensionsAndValues(filter);
  const blacklistDimensions = DIMENSION_BLACK_LIST.filter(
    dimension => R.path([dimension, 'length'], dimensionsToValues) > 0
  );
  if (touchTypes.length < 1 || blacklistDimensions.length < 1) {
    return;
  }
  const humanReadableDataSources = touchTypes.map(
    type => DRUID_TO_HUMAN_READABLE[type] || type
  );
  const humanReadableDimensions = blacklistDimensions.map(
    dimension => DRUID_TO_HUMAN_READABLE[dimension] || dimension
  );

  return `This filter does not filter on ${humanReadableDimensions.join(
    ' or '
  )} for ${humanReadableDataSources.join(' or ')}, which do not have ${
    blacklistDimensions.length > 1 ? '' : 'a'
  } ${humanReadableDimensions.join(' or ')}.`;
}
